import {
  Group,
  Icon,
  Loader,
  SecondaryButton,
  Tabs,
  TabsList,
  TabsTrigger,
  Title1,
  VerticalSpacing,
} from "@einride/ui"
import styled from "@emotion/styled"
import { SortingState } from "@tanstack/react-table"
import { DATE_VALUE_FORMAT } from "components/DatePicker/format"
import { chartColorPalette } from "components/graphs/utils/color"
import { ShipmentLayout } from "components/ShipmentLayout"
import { stringify } from "csv-stringify/browser/esm/sync"
import saveAs from "file-saver"
import { formatCustomRange } from "lib/date"
import { useQueryParamWrapper } from "lib/hooks/useQueryParamWrapper"
import { DateTimeParam, FilterSelectionParam } from "lib/params"
import { translateMetricId } from "lib/translations"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useInfiniteScroll from "react-infinite-scroll-hook"
import { BooleanParam, StringParam, useQueryParam, withDefault } from "use-query-params"
import { Sidebar } from "./components/Sidebar"
import { TableData } from "./components/Table"
import { useShipmentLegsCount } from "./useShipmentCount"
import { useShipmentLegs } from "./useShipmentLegs"

// eslint-disable-next-line import/no-default-export
export default (): React.JSX.Element => {
  const { t } = useTranslation()
  const [isSplitViewOpen, setIsSplitViewOpen] = useQueryParamWrapper(
    "splitView",
    withDefault(BooleanParam, true),
  )
  const [reportId] = useQueryParam("reportId", StringParam)
  const [filterSelection] = useQueryParam("filterSelection", FilterSelectionParam)
  const [metricId] = useQueryParam("metricId", StringParam)
  const [groupAttributeId] = useQueryParam("groupAttributeId", StringParam)
  const [fromDate] = useQueryParam("fromDate", DateTimeParam)
  const [tableSorting, setTableSorting] = useState<SortingState>([])
  const [toDate] = useQueryParam("toDate", DateTimeParam)
  const [groupValue, setGroupValue] = useState<string | undefined>()
  const {
    data: shipmentLegs,
    loading: shipmentLegsLoading,
    error,
    hasNextPage,
    loadMore,
  } = useShipmentLegs(
    metricId ?? "",
    [
      ...filterSelection,
      ...(groupValue && groupAttributeId
        ? [{ filterAttributeId: groupAttributeId, filterTermIds: [groupValue] }]
        : []),
    ],
    fromDate,
    toDate,
    tableSorting.map((sorting) => ({ attributeId: sorting.id, descending: sorting.desc })),
  )
  const { data: shipmentLegsCount, loading: shipmentLegsCountLoading } = useShipmentLegsCount(
    filterSelection,
    fromDate,
    toDate,
    groupAttributeId,
  )

  const loading = shipmentLegsLoading || shipmentLegsCountLoading

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  const handleDownloadClicked = (): void => {
    const output = stringify([
      [
        t("shipmentLegsPage.shipmentId"),
        t("shipmentLegsPage.referenceId"),
        t("shipmentLegsPage.releasedAt"),
        t("shipmentLegsPage.deliveredAt"),
        t("shipmentLegsPage.originLocation"),
        t("shipmentLegsPage.originCountry"),
        t("shipmentLegsPage.originCity"),
        t("shipmentLegsPage.originPostalCode"),
        t("shipmentLegsPage.destinationLocation"),
        t("shipmentLegsPage.destinationCountry"),
        t("shipmentLegsPage.destinationCity"),
        t("shipmentLegsPage.destinationPostalCode"),
        t("shipmentLegsPage.driveType"),
        t("shipmentLegsPage.energySource"),
      ],
      ...shipmentLegs.map((row) => [
        row.shipment,
        row.shipmentExternalReferenceId,
        row.shipmentReleaseTime,
        row.deliveryReportedCompleteTime,
        row.originLocationDisplayName,
        row.originCountryCode,
        row.originCity,
        row.originPostalCode,
        row.destinationLocationDisplayName,
        row.destinationCountryCode,
        row.destinationCity,
        row.destinationPostalCode,
        row.vehicleDriveType,
        row.vehicleEnergySource,
      ]),
    ])
    const blob = new Blob([output], { type: "text/csv;charset=utf-8" })
    saveAs(blob, `shipment-legs-${fromDate.toFormat(DATE_VALUE_FORMAT)}.csv`)
  }

  return (
    <>
      <ShipmentLayout
        isSplitViewOpen={isSplitViewOpen}
        setIsSplitViewOpen={setIsSplitViewOpen}
        backLink={`/reports/${reportId}`}
        actions={
          <SecondaryButton onClick={handleDownloadClicked} rightIcon={<Icon name="arrowDown" />}>
            {t("common.download")}
          </SecondaryButton>
        }
      >
        <Group>
          <Title1>{t("shipmentLegsPage.shipmentView")}</Title1>
          <Title1 color="secondary">{translateMetricId(metricId ?? "", t)}</Title1>
        </Group>
        <VerticalSpacing size="lg" />
        <ActionGroup
          alignItems="center"
          justifyContent="right"
          paddingTop="xs"
          paddingBottom="xs"
          borderTop
        >
          <SecondaryButton>{formatCustomRange(fromDate, toDate)}</SecondaryButton>
        </ActionGroup>
        {!shipmentLegsCountLoading && shipmentLegsCount.length > 1 && (
          <GroupList>
            <div>{t("shipmentLegsPage.groups")}:</div>
            <StyledTabs onValueChange={setGroupValue}>
              <StyledTabsList>
                {shipmentLegsCount.map(({ group, count }, index) => (
                  <StyledTabsTrigger key={group} value={group ?? ""}>
                    <SvgCircle>
                      <circle
                        cx="50%"
                        cy="50%"
                        r="6"
                        fill={chartColorPalette[index % chartColorPalette.length]}
                      />
                    </SvgCircle>
                    {group} ({count})
                  </StyledTabsTrigger>
                ))}
              </StyledTabsList>
            </StyledTabs>
          </GroupList>
        )}
        <TableData
          shipmentLegs={shipmentLegs}
          sorting={tableSorting}
          setSorting={setTableSorting}
          footer={
            (shipmentLegsLoading || hasNextPage) && (
              <div>
                <VerticalSpacing size="md" />
                <Loader ref={sentryRef} />
                <VerticalSpacing size="md" />
              </div>
            )
          }
        />
      </ShipmentLayout>
      <Sidebar
        isOpen={isSplitViewOpen}
        onSidebarClick={() => setIsSplitViewOpen(!isSplitViewOpen)}
        filterSelection={filterSelection}
        metricId={metricId}
        groupAttributeId={groupAttributeId}
        loading={false}
      />
    </>
  )
}

const ActionGroup = styled(Group)<{ borderTop?: boolean }>`
  border-top: ${({ borderTop: borderBottom, theme }) =>
    borderBottom ? `1px solid ${theme.colors.border.primary}` : "none"};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`

const StyledTabsList = styled(TabsList)`
  padding-block: 0;
  margin-block: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
`

const StyledTabsTrigger = styled(TabsTrigger)`
  display: flex;
  padding-block: 1.2rem;
  align-items: center;
  box-shadow: none;
  width: max-content;
`

const SvgCircle = styled.svg`
  width: 1rem;
  min-width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
`

const GroupList = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  align-items: center;
  gap: 1.5rem;
  box-shadow: inset 0 -0.0625rem 0 #dadada;
`

const StyledTabs = styled(Tabs)`
  overflow-x: auto;
`
